import { EVENT_POPIN_OPEN } from '@kissui/components'
import { NespressoElement } from '@kissui/components/src/nespressoElement'
import { renderLabels } from './fragments/labels'
import { renderLogos } from '@kissui/helpers/src/logos.helpers'
import { renderImage } from './fragments/image'
import { renderHeading } from './fragments/heading'
import { renderHeadline } from './fragments/headline'
import { renderLabelTechnology } from './fragments/labelTechnology'
import { renderLabelRange } from './fragments/labelRange'
import { renderQuickView } from './fragments/quickView'
import { renderStrikethroughPrice } from './fragments/strikethroughPrice'
import { renderPrice } from './fragments/price'
import { renderAddToCart } from './fragments/addToCart'
import { renderAdditionalMessage } from './fragments/additionalMessage'
import { setCardTheme } from './fragments/setCardTheme'
import { getSkuQuickViewData } from '@kissui/components/src/sku-quick-view/shared-quick-view'
import { dispatchEvent } from '@kissui/helpers/src/assets/js/eventDispatch'
import { getTechnologyName, getProduct } from '@kissui/helpers/src/catalog'
import { trackComponentInteraction, productClick } from '@kissui/helpers/src/gtmEvents'
import { isLoggedIn } from '@kissui/helpers/src/dataLayer'
import { VARIATION_PLP } from '@kissui/components/src/sku/constants'

class Sku extends NespressoElement {
    constructor() {
        super()
        this.type = 'default'
        this.popinId = ''
        this.isQuickViewPlp = false
        this.boundOnPDPLinkClick = this.onPDPLinkClick.bind(this)
        this.boundOnQuickViewOpen = this.onQuickViewOpen.bind(this)
        this.clickMethod = this.boundOnPDPLinkClick
    }

    loadTemplate() {
        this.classList.add('nb-sku')
        this.classList.add(`nb-sku__${this.type}`)
        setCardTheme(this.props, this)

        Object.assign(this.props, this.getSleevePrice(this.props))
        Object.assign(this.props, this.checkHiddenPriceForLoggedInUser(this.props))

        const { quick_view_plp = {} } = this.props
        this.isQuickViewPlp = quick_view_plp?.show_view || false
        this.clickMethod = this.isQuickViewPlp
            ? this.boundOnQuickViewOpen
            : this.boundOnPDPLinkClick

        this.template = `${this.renderVisual()}
                          ${this.renderHeader()}
                          <div class="cb-content">
                              ${this.renderInformation()}
                              ${this.isQuickViewPlp ? this.renderQuickView() : ''}
                              <div class="cb-shop">
                                  <div class="cb-price">
                                      ${this.renderPricing()}
                                  </div>
                                  ${this.renderAction()}
                              </div>
                              ${this.renderFooter()}
                          </div>`

        this.render()
    }

    onReady() {
        const { activeSku, sku } = this.props
        this.setAttribute('data-product-short-sku', activeSku || sku)
        this.bindEvent()
    }

    renderHeaderDefault() {
        return `${renderLabels(this.props, this)}${renderLogos(this.props, this)}`
    }

    renderHeader() {
        return this.renderHeaderDefault()
    }

    renderVisualDefault() {
        const theme = this.hasAttribute('portrait') ? 'portrait' : 'landscape'
        return renderImage(this.props, theme)
    }

    getSleevePrice({ number_of_sleeves, price }) {
        let hasPricePerSleeve = true
        let sleevePrice
        if (number_of_sleeves === '1') {
            sleevePrice = price
        } else {
            hasPricePerSleeve = false
        }
        return { hasPricePerSleeve, sleevePrice }
    }

    checkHiddenPriceForLoggedInUser({ hidePrice, show_view_details }) {
        return {
            hidePrice: isLoggedIn() ? false : hidePrice,
            show_view_details: isLoggedIn() ? false : show_view_details
        }
    }

    renderVisual() {
        return this.renderVisualDefault()
    }

    renderInformationDefault() {
        return `${renderLabelTechnology(this.props)}
        ${renderLabelRange(this.props)}
        ${renderHeading(this.props)}
        ${renderHeadline(this.props)}`
    }

    renderInformation() {
        return this.renderInformationDefault()
    }

    renderQuickView() {
        return renderQuickView(this.props)
    }

    renderPricingDefault() {
        const isPortrait = this.hasAttribute('portrait')

        const pricingWithClass = `
        ${renderStrikethroughPrice(this.props, this)}
        <div class="${isPortrait ? 't-md-700-sl' : 't-sm-700-sl'}">
            ${renderPrice(this.props)}
        </div>`

        return pricingWithClass
    }

    renderPricing() {
        return this.renderPricingDefault()
    }

    renderActionDefault() {
        return renderAddToCart(this.props, false, this)
    }

    renderAction() {
        const actionSlot = this.querySelector('[slot="action"]')?.outerHTML
        if (actionSlot) {
            return actionSlot
        }
        return this.renderActionDefault()
    }

    renderFooterDefault() {
        return renderAdditionalMessage(this.props)
    }

    renderFooter() {
        return this.renderFooterDefault()
    }

    async onPDPLinkClick() {
        const productIndex = this.getAttribute('tracking_position') || 0
        const list = this.getAttribute('tracking_list') || ''

        await productClick(productIndex, list, this.props.longSku)
    }

    renderSkuQuickView(product) {
        this.popinId = `plp-quick-view`

        let quickViewElement = document.querySelector(`#${this.popinId}`)
        if (quickViewElement) {
            quickViewElement.remove()
        }

        getSkuQuickViewData(product, this.popinId, this.props, VARIATION_PLP)
    }

    async onQuickViewOpen(e) {
        e.preventDefault()

        const { sku } = this.props
        if (!sku) {
            return
        }

        try {
            const product = await getProduct(sku)

            this.renderSkuQuickView(product)
            trackComponentInteraction({
                creative: this.props.campaign?.creative,
                actionType: 'pdp quick view',
                internationalId: product.internationalId,
                internationalName: product.internationalName,
                productType: product.type,
                technology: getTechnologyName(product),
                category: product.category,
                rawPrice: product.price,
                eventAction: 'PDP Quick View'
            })
            dispatchEvent({ eventName: EVENT_POPIN_OPEN, args: { id: this.popinId } })
        } catch (e) {
            console.error(`getProduct fail on sku ${sku}`)
        }
        return false
    }

    bindEvent() {
        this.unbindEvent()
        const showQuickView = this.props.quick_view_plp?.show_view
        const quickViewTextExists = this.props.quick_view_plp?.quick_view_text

        if (!quickViewTextExists) {
            this.classList.add('sku-hover')
        }

        if (showQuickView && this.props.variation === VARIATION_PLP) {
            const elements = quickViewTextExists ? this.querySelectorAll('nb-link button') : [this]

            elements.forEach(element => {
                element.addEventListener('click', this.clickMethod)
            })

            // to avoid adding click event to the button inside the sku card
            this.addStopPropagationEvent('nb-add-to-cart')
            this.addStopPropagationEvent('nb-cta')
        }
    }

    unbindEvent() {
        const showQuickView = this.props.quick_view_plp?.show_view
        const quickViewTextExists = this.props.quick_view_plp?.quick_view_text

        if (showQuickView && this.props.variation === VARIATION_PLP) {
            const elements = quickViewTextExists ? this.querySelectorAll('nb-link button') : [this]

            elements.forEach(element => {
                element.removeEventListener('click', this.clickMethod)
            })

            this.removeStopPropagationEvent('nb-add-to-cart')
            this.removeStopPropagationEvent('nb-cta')
        }
    }

    addStopPropagationEvent(selector) {
        const element = this.querySelector(selector)
        if (element) {
            element.addEventListener('click', this.stopPropagationHandler)
        }
    }

    removeStopPropagationEvent(selector) {
        const element = this.querySelector(selector)
        if (element) {
            element.removeEventListener('click', this.stopPropagationHandler)
        }
    }

    stopPropagationHandler(event) {
        event.stopPropagation()
    }

    disconnectedCallback() {
        this.unbindEvent()
    }
}

customElements.get('nb-sku') || customElements.define('nb-sku', Sku)

export default Sku
